import { ensureValidToken } from '../../auth/keycloak';
import { API_CONFIG } from '../../config/env';
import { logger } from '../../utils/logger';
import { FE_TELEMETRY_SCHEMA_VERSION, type FePageContext, type FeTelemetryBatch, type FeTelemetryEvent } from './types';
import { resolvePageContext } from './routeContext';

const EVENTS_PATH = '/api/client-telemetry/events';
const FLUSH_INTERVAL_MS = 4000;
const MAX_BATCH_EVENTS = 40;
const MAX_QUEUE_EVENTS = 200;

let sessionId: string | null = null;
let nativeFetch: typeof fetch | null = null;
let queue: FeTelemetryEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let currentPageCtx: FePageContext = { path: '/', route: '/' };

function getSessionId(): string {
  if (sessionId) return sessionId;
  if (typeof window !== 'undefined') {
    const stored = sessionStorage.getItem('fe_telemetry_sid');
    if (stored) {
      sessionId = stored;
      return stored;
    }
    const created = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `sid-${Date.now()}`;
    sessionStorage.setItem('fe_telemetry_sid', created);
    sessionId = created;
    return created;
  }
  sessionId = `sid-${Date.now()}`;
  return sessionId;
}

export function setFeTelemetryNativeFetch(fetchImpl: typeof fetch) {
  nativeFetch = fetchImpl;
}

export function updateFePageContext(pathname: string): FePageContext {
  currentPageCtx = resolvePageContext(pathname);
  return currentPageCtx;
}

export function enqueueFeEvent(event: FeTelemetryEvent) {
  if (typeof window === 'undefined') return;
  if (queue.length >= MAX_QUEUE_EVENTS) {
    queue.shift();
  }
  queue.push(event);
  scheduleFlush();
}

export function flushFeTelemetry(force = false) {
  if (queue.length === 0) return;
  if (!force && queue.length < MAX_BATCH_EVENTS && flushTimer != null) {
    return;
  }

  const events = queue.splice(0, MAX_BATCH_EVENTS);
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  const batch: FeTelemetryBatch = {
    v: FE_TELEMETRY_SCHEMA_VERSION,
    sid: getSessionId(),
    sentAt: new Date().toISOString(),
    ctx: currentPageCtx,
    events,
  };

  void sendBatch(batch);
  if (queue.length > 0) {
    scheduleFlush();
  }
}

function scheduleFlush() {
  if (flushTimer != null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushFeTelemetry(true);
  }, FLUSH_INTERVAL_MS);
}

async function sendBatch(batch: FeTelemetryBatch) {
  try {
    const token = await ensureValidToken(10);
    if (!token) {
      return;
    }

    const fetchImpl = nativeFetch || window.fetch.bind(window);
    await fetchImpl(`${API_CONFIG.baseUrl}${EVENTS_PATH}`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Telemetry-Source': 'frontend-events',
      },
      body: JSON.stringify(batch),
    });
  } catch (error) {
    logger.debug('FE telemetry batch send failed', error);
  }
}

export function initFeTelemetryUnloadFlush() {
  if (typeof window === 'undefined') return;
  window.addEventListener('pagehide', () => flushFeTelemetry(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushFeTelemetry(true);
    }
  });
}
